// Reusable video upload flow hook: upload video, build S3-based thumbnails, and save selected thumbnail.
import { useMemo, useState } from "react";
import {
  getImagePresignedUrl,
  getVideoPresignedUrl,
  updateVideoThumbnail,
  uploadImage,
  uploadVideoWithThumbnail,
} from "api/common/Data.api";
import type { VideoUploadFlowPhase } from "./VideoUploadFlowPopup.component";

const DEFAULT_PHASE: VideoUploadFlowPhase = "uploading";

function dataUrlToFile(dataUrl: string, fileName: string): File | null {
  try {
    const [meta, base64] = dataUrl.split(",");
    if (!meta || !base64) return null;
    const mimeMatch = /data:(.*?);base64/.exec(meta);
    const mime = mimeMatch?.[1] || "image/jpeg";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], fileName, { type: mime });
  } catch {
    return null;
  }
}

async function extractVideoThumbnailCandidates(
  videoUrl: string,
  count = 20
): Promise<string[]> {
  const video = document.createElement("video");
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = "anonymous";
  video.src = videoUrl;
  video.load();

  await new Promise<void>((resolve, reject) => {
    let done = false;
    const finish = (error?: Error) => {
      if (done) return;
      done = true;
      video.onloadedmetadata = null;
      video.onloadeddata = null;
      video.onerror = null;
      if (error) reject(error);
      else resolve();
    };
    const timer = window.setTimeout(
      () => finish(new Error("metadata load timeout")),
      5000
    );
    video.onloadedmetadata = () => {
      window.clearTimeout(timer);
      finish();
    };
    video.onloadeddata = () => {
      window.clearTimeout(timer);
      finish();
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      finish(new Error("failed to load metadata"));
    };
  });

  const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 1;
  const sampleCount = Math.max(1, count);
  const times = Array.from({ length: sampleCount }, (_, idx) => {
    if (sampleCount <= 1) return 0;
    const ratio = idx / (sampleCount - 1);
    return Math.min(duration - 0.01, Math.max(0, duration * ratio));
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, video.videoWidth || 640);
  canvas.height = Math.max(1, video.videoHeight || 360);
  const ctx = canvas.getContext("2d");
  if (!ctx) return [];

  const thumbs: string[] = [];
  for (const t of times) {
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        video.onseeked = null;
        video.onerror = null;
        resolve();
      };
      const timer = window.setTimeout(finish, 1500);
      video.onseeked = () => {
        window.clearTimeout(timer);
        finish();
      };
      video.onerror = () => {
        window.clearTimeout(timer);
        finish();
      };
      try {
        if (Math.abs(video.currentTime - t) < 0.01) {
          window.clearTimeout(timer);
          finish();
          return;
        }
        video.currentTime = t;
      } catch {
        window.clearTimeout(timer);
        finish();
      }
    });
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      thumbs.push(canvas.toDataURL("image/jpeg", 0.88));
    } catch {
      // ignore frame capture failure
    }
  }
  return thumbs.filter(Boolean);
}

async function extractVideoThumbnailCandidatesFromFile(
  file: File,
  count = 20
): Promise<string[]> {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await extractVideoThumbnailCandidates(objectUrl, count);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function isIosClient(): boolean {
  const ua = navigator.userAgent || "";
  const iOSPattern = /iPad|iPhone|iPod/i.test(ua);
  const iPadOs13Up =
    /Macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  return iOSPattern || iPadOs13Up;
}

type MessagePack = {
  preparing: string;
  uploading: string;
  uploadError: string;
  thumbnailing?: string;
};

type UploadState = {
  isVisible: boolean;
  phase: VideoUploadFlowPhase;
  progress: number;
  currentStep: string;
  canCancel: boolean;
  thumbnails: string[];
  selectedIndex: number;
  errorMessage: string;
  isSaving: boolean;
  uploadedVideoId: string | null;
  uploadedVideoUrl: string | null;
  isUploading: boolean;
};

const initialState = (): UploadState => ({
  isVisible: false,
  phase: DEFAULT_PHASE,
  progress: 0,
  currentStep: "",
  canCancel: false,
  thumbnails: [],
  selectedIndex: 0,
  errorMessage: "",
  isSaving: false,
  uploadedVideoId: null,
  uploadedVideoUrl: null,
  isUploading: false,
});

export function useVideoUploadFlow() {
  const [state, setState] = useState<UploadState>(initialState);

  const openUploading = (preparingText: string) => {
    setState((prev) => ({
      ...prev,
      isVisible: true,
      phase: "uploading",
      progress: 0,
      currentStep: preparingText,
      canCancel: false,
      errorMessage: "",
      isUploading: true,
    }));
  };

  const close = () => {
    setState((prev) => ({ ...prev, isVisible: false }));
  };

  const reset = () => {
    setState(initialState());
  };

  const selectThumbnail = (index: number) => {
    setState((prev) => ({ ...prev, selectedIndex: index }));
  };

  const startUpload = async (
    file: File,
    width: number,
    height: number,
    message: MessagePack
  ) => {
    openUploading(message.preparing);
    try {
      const response = await uploadVideoWithThumbnail(
        file,
        width,
        height,
        false,
        undefined,
        true,
        (progressEvent) => {
          const total = progressEvent.total || file.size || 1;
          const percent = Math.round((progressEvent.loaded * 100) / total);
          setState((prev) => ({
            ...prev,
            currentStep: message.uploading,
            progress: percent,
          }));
        }
      );
      if (!response?.id) throw new Error("missing video id");
      setState((prev) => ({
        ...prev,
        currentStep: message.thumbnailing || message.uploading,
      }));

      const presigned = await getVideoPresignedUrl(response.id);
      if (!presigned?.result) throw new Error("missing presigned url");

      const useLocalFirst = isIosClient();
      let thumbnails = useLocalFirst
        ? await extractVideoThumbnailCandidatesFromFile(file, 20)
        : await extractVideoThumbnailCandidates(presigned.result, 20);
      if (!thumbnails.length) {
        thumbnails = useLocalFirst
          ? await extractVideoThumbnailCandidates(presigned.result, 20)
          : await extractVideoThumbnailCandidatesFromFile(file, 20);
      }
      if (!thumbnails.length && (response as any)?.thumbnailImageId) {
        try {
          const thumbnailInfo = await getImagePresignedUrl(
            (response as any).thumbnailImageId
          );
          if (thumbnailInfo?.result) {
            thumbnails = [thumbnailInfo.result];
          }
        } catch {
          // keep empty
        }
      }
      if (!thumbnails.length) throw new Error("no thumbnail candidates");

      setState((prev) => ({
        ...prev,
        phase: "selecting",
        progress: 100,
        currentStep: "",
        canCancel: false,
        thumbnails,
        selectedIndex: 0,
        uploadedVideoId: response.id,
        uploadedVideoUrl: presigned.result,
        isUploading: false,
      }));
      return {
        videoId: response.id,
        videoUrl: presigned.result,
      };
    } catch (e) {
      setState((prev) => ({
        ...prev,
        phase: "error",
        errorMessage: message.uploadError,
        isUploading: false,
      }));
      throw e;
    }
  };

  const saveSelectedThumbnail = async (uploadErrorText: string) => {
    const { uploadedVideoId, thumbnails, selectedIndex } = state;
    if (!uploadedVideoId) throw new Error("no uploaded video");

    const selected = thumbnails[selectedIndex];
    const thumbFile = selected
      ? dataUrlToFile(selected, `video-thumb-${Date.now()}.jpg`)
      : null;
    if (!thumbFile) throw new Error("invalid thumbnail file");

    try {
      setState((prev) => ({ ...prev, isSaving: true }));
      const uploadedImage = await uploadImage(thumbFile, true);
      if (uploadedImage?.id) {
        try {
          await updateVideoThumbnail(uploadedVideoId, uploadedImage.id);
        } catch (e: any) {
          if (e?.response?.status !== 404) throw e;
        }
      }
      setState((prev) => ({ ...prev, isSaving: false, isVisible: false }));
      return { videoId: uploadedVideoId, thumbnailImageId: uploadedImage?.id || null };
    } catch (e) {
      setState((prev) => ({
        ...prev,
        phase: "error",
        errorMessage: uploadErrorText,
        isSaving: false,
      }));
      throw e;
    }
  };

  const popupProps = useMemo(
    () => ({
      isVisible: state.isVisible,
      phase: state.phase,
      progress: state.progress,
      currentStep: state.currentStep,
      canCancel: state.canCancel,
      errorMessage: state.errorMessage,
      thumbnails: state.thumbnails,
      selectedIndex: state.selectedIndex,
      isSaving: state.isSaving,
    }),
    [state]
  );

  return {
    state,
    popupProps,
    startUpload,
    selectThumbnail,
    saveSelectedThumbnail,
    close,
    reset,
  };
}

